import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationLocalAssaysAssaygroupsLaboratoryModal from '@/modules/configurationModule/detail/local-assays/assaygroups-laboratory/components/modal/configuration-local-assays-assaygroups-laboratory-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationLocalAssaysAssaygroupsLaboratoryGridState, initialConfigurationLocalAssaysAssaygroupsLaboratoryGridState } from '@/modules/configurationModule/detail/local-assays/assaygroups-laboratory/components/grid/configuration-local-assays-assaygroups-laboratory-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { ConfigurationAssaygroupLaboratoryCreateRequest, ConfigurationAssaygroupLaboratoryPatchInBatchRequest, ConfigurationAssaygroupLaboratoryResponse } from '@/modules/configurationModule/detail/local-assays/assaygroups-laboratory/services/configuration-assaygroup-laboratory/configuration-assaygroup-laboratory.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationLocalAssaysAssaygroupsLaboratoryModal
    }
})
export default class ConfigurationLocalAssaysAssaygroupsLaboratoryGrid extends BaseCrudServerSideComponent<ConfigurationAssaygroupLaboratoryResponse> implements BaseCrud {
    @Ref() readonly assaygroupsLaboratoryModalWrapper!: typeof ModalWrapper

    state: ConfigurationLocalAssaysAssaygroupsLaboratoryGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysAssaygroupsLaboratoryGridState()
    }

    /// SERVICES CALLS ///

    private async _createAssaygroupLaboratory(createRequest: ConfigurationAssaygroupLaboratoryCreateRequest): Promise<ConfigurationAssaygroupLaboratoryResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchAssaygroupsLaboratory(batchRequests: ConfigurationAssaygroupLaboratoryPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteAssaygroupsLaboratory(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationAssaygroupLaboratoryCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAssaygroupLaboratory(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationAssaygroupLaboratoryPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAssaygroupsLaboratory([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAssaygroupsLaboratory(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(assaygroupLaboratory: ConfigurationAssaygroupLaboratoryResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (assaygroupLaboratory) {
            this.showModal(this.$tc('configuration.actions.update'), assaygroupLaboratory, this.assaygroupsLaboratoryModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assaygroupLaboratory => assaygroupLaboratory.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.assaygroupsLaboratoryModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
